import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { PageTree } from "../components/page-tree"
import { Markdown } from "../components/markdown"
import { ChevronDown } from "../components/icons"
import type { PageEditorHandle } from "../components/page-editor"
import { differences } from "../lib/markdown-diff"
import {
  Badge,
  Button,
  Card,
  Dropdown,
  Empty,
  ErrorNote,
  Field,
  InlineEdit,
  type MenuItem,
  Modal,
  SegmentedControl,
  Skeleton,
  formatDate,
  useLoad,
  useToast,
} from "../components/ui"
import { type Page, type ResolvedLink, api } from "../lib/api"
import { type AttachmentRef, attachmentRef, resolveAttachmentHref } from "../lib/attachment-refs"

// One wiki page: the tree on the left, the page on the right, its properties
// under that.
//
// The editor is behind React.lazy because it is by far the heaviest thing this
// app can load — nobody opening the board or the dashboard should pay for a
// wiki editor they never open.
const PageEditor = lazy(() => import("../components/page-editor"))

// `assets` are the images the body refers to as `attachment:<id>`, each with a
// URL good for an hour from when the page was fetched.
type PageResponse = { page: Page; links: ResolvedLink[]; assets: AttachmentRef[] }
type PageList = { pages: { id: string; path: string; title: string }[] }

export function WikiPageView() {
  const { pageId = "" } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const loaded = useLoad(() => api.get<PageResponse>(`/context-hub/pages/${pageId}`), [pageId])
  const page = loaded.data?.page

  const siblings = useLoad(
    () =>
      page
        ? api.get<PageList>(`/context-hub/spaces/${page.spaceId}/pages`)
        : Promise.resolve({ pages: [] }),
    [page?.spaceId],
  )

  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)

  // The server resolved each internal href to the page it reaches, so this is
  // a lookup rather than a second implementation of path arithmetic. A link
  // whose target does not exist stays plain text instead of leading to a 404.
  const routeFor = (href: string) => {
    const link = loaded.data?.links.find((l) => l.href === href)
    return link?.targetId ? `/context-hub/pages/${link.targetId}` : null
  }
  // An image stored in our bucket is an `attachment:` ref in the body; it is
  // turned into a URL here, at paint time, and anything else is a link.
  const resolveHref = (href: string) =>
    resolveAttachmentHref(loaded.data?.assets ?? [])(href) ?? routeFor(href)

  if (loaded.loading) return <Skeleton className="h-96" />
  if (loaded.error) return <ErrorNote error={loaded.error} />
  if (!page) return <Empty title="Page not found" />

  const save = async (body: Record<string, unknown>, message?: string) => {
    try {
      await api.patch(`/context-hub/pages/${pageId}`, body)
      if (message) toast(message)
      loaded.reload()
      siblings.reload()
    } catch (error) {
      toast(error instanceof Error ? error.message : "Could not save that", "error")
    }
  }

  const actions: MenuItem[] = [
    { label: "Move or rename…", onSelect: () => setMoving(true) },
    {
      label: "Index for search",
      onSelect: async () => {
        try {
          await api.post(`/context-hub/pages/${pageId}/embed`, {})
          toast("Indexed")
          loaded.reload()
        } catch (error) {
          toast(error instanceof Error ? error.message : "Could not index that", "error")
        }
      },
    },
    page.deletedAt
      ? {
          label: "Restore",
          onSelect: async () => {
            await api.post(`/context-hub/pages/${pageId}/restore`, {})
            toast("Page restored")
            loaded.reload()
          },
        }
      : {
          label: "Delete",
          danger: true,
          onSelect: async () => {
            // Soft delete: the page is cited by agents and linked from other
            // pages, so the row stays reachable and can come back.
            await api.delete(`/context-hub/pages/${pageId}`)
            toast("Page deleted")
            loaded.reload()
          },
        },
  ]

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
      <aside className="w-full shrink-0 lg:sticky lg:top-8 lg:max-h-[calc(100vh-6rem)] lg:w-64 lg:overflow-y-auto">
        <PageTree pages={siblings.data?.pages ?? []} currentId={page.id} currentPath={page.path} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <header className="flex flex-col gap-2 border-b border-border pb-4">
          <Breadcrumb page={page} />

          <div className="flex flex-wrap items-start justify-between gap-3">
            <InlineEdit
              ariaLabel="Page title"
              value={page.title}
              className="text-2xl font-semibold tracking-tight text-foreground"
              inputClassName="text-2xl font-semibold tracking-tight text-foreground"
              onSave={(title) => (title ? save({ title }, "Title saved") : undefined)}
            />

            <div className="flex shrink-0 items-center gap-2">
              <SegmentedControl
                ariaLabel="Mode"
                value={editing ? "edit" : "read"}
                onChange={(next) => setEditing(next === "edit")}
                options={[
                  { value: "read", label: "Read" },
                  { value: "edit", label: "Edit" },
                ]}
              />
              <Dropdown
                align="right"
                items={actions}
                trigger={({ toggle }) => (
                  <Button variant="ghost" size="sm" onClick={toggle}>
                    Actions <ChevronDown className="size-3" />
                  </Button>
                )}
              />
            </div>
          </div>

          <PageStatus page={page} onChange={loaded.reload} />
        </header>

        {editing ? (
          <Body
            key={page.updatedAt}
            page={page}
            assets={loaded.data?.assets ?? []}
            onDone={() => {
              setEditing(false)
              loaded.reload()
            }}
          />
        ) : (
          <Card className="p-6">
            <Markdown className="text-foreground" resolveHref={resolveHref}>
              {page.content}
            </Markdown>
          </Card>
        )}

        <Properties page={page} onSave={save} />
      </div>

      {moving && (
        <MoveDialog
          page={page}
          onClose={() => setMoving(false)}
          onMoved={() => {
            setMoving(false)
            loaded.reload()
            siblings.reload()
            navigate(`/context-hub/pages/${page.id}`)
          }}
        />
      )}
    </div>
  )
}

function Breadcrumb({ page }: { page: Page }) {
  const segments = page.path.split("/").filter(Boolean)
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-faint">
      <Link to="/context-hub" className="hover:text-muted hover:underline">
        Context Hub
      </Link>
      <span>/</span>
      <Link to={`/context-hub/spaces/${page.spaceId}`} className="hover:text-muted hover:underline">
        Space
      </Link>
      {segments.slice(0, -1).map((segment) => (
        <span key={segment} className="flex items-center gap-1.5">
          <span>/</span>
          <span className="font-mono">{segment}</span>
        </span>
      ))}
      <span>/</span>
      <span className="font-mono text-muted">{segments.at(-1)}</span>
    </div>
  )
}

/** Whether the page is searchable, and whether anyone still stands behind it. */
function PageStatus({ page, onChange }: { page: Page; onChange: () => void }) {
  const [busy, setBusy] = useState(false)
  const stale = page.expiresAt !== null && new Date(page.expiresAt) < new Date()

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      {page.deletedAt && <Badge tone="warning">Deleted</Badge>}

      {page.embedStatus === "embedded" && <span className="text-muted">Searchable by meaning</span>}
      {page.embedStatus === "blocked" && (
        <span className="text-danger">Not indexed — contains something that looks like a secret</span>
      )}
      {page.embedStatus === "failed" && <span className="text-danger">Indexing failed</span>}
      {!page.embedStatus && <span className="text-muted">Not indexed — found by keyword only</span>}

      {stale ? (
        <span className="flex items-center gap-2 text-warning">
          Past its review date
          <button
            type="button"
            disabled={busy}
            className="underline underline-offset-2 hover:text-foreground"
            onClick={async () => {
              setBusy(true)
              try {
                // Confirming a page is correct is not editing it, so this
                // restarts the clock without touching the content.
                await api.post(`/context-hub/pages/${page.id}/reviewed`, {})
                onChange()
              } finally {
                setBusy(false)
              }
            }}
          >
            still correct
          </button>
        </span>
      ) : page.expiresAt ? (
        <span className="text-muted">Review by {formatDate(page.expiresAt)}</span>
      ) : null}
    </div>
  )
}

// ---------- editing ----------

function Body({
  page,
  assets,
  onDone,
}: {
  page: Page
  assets: AttachmentRef[]
  onDone: () => void
}) {
  const toast = useToast()

  // Images uploaded while editing. They are drafts in the bucket until a save
  // whose body refers to them claims them for the page; the same mechanism as
  // a comment's inline images (CommentEditor).
  const [uploads, setUploads] = useState<AttachmentRef[]>([])
  const refs = useMemo(() => [...assets, ...uploads], [assets, uploads])
  // Read through a ref: Crepe captures proxyDomURL once per image node, and a
  // node mounted right after an upload has to see the upload it came from.
  const refsRef = useRef(refs)
  useEffect(() => {
    refsRef.current = refs
  }, [refs])

  const uploadImage = useCallback(
    async (file: File) => {
      const form = new FormData()
      form.append("file", file)
      const uploaded = await api.postMultipart<{ id: string; url: string }>(
        `/context-hub/spaces/${page.spaceId}/assets`,
        form,
      )
      // Synchronously, not only through the effect above: Crepe asks
      // proxyDomURL for the new image's URL the moment this returns, before
      // React has re-rendered — and an unresolved `attachment:` src is a
      // broken image that stays broken until the editor remounts.
      refsRef.current = [...refsRef.current, uploaded]
      setUploads((current) => [...current, uploaded])
      // The ref goes into the markdown, never the signed URL, which expires
      // within the hour.
      return attachmentRef(uploaded.id)
    },
    [page.spaceId],
  )
  const proxyDomURL = useCallback((url: string) => resolveAttachmentHref(refsRef.current)(url) ?? url, [])
  const [mode, setMode] = useState<"blocks" | "markdown">("blocks")
  const [draft, setDraft] = useState(page.content ?? "")
  const [busy, setBusy] = useState(false)
  const [lossy, setLossy] = useState<{ next: string; diff: ReturnType<typeof differences> } | null>(
    null,
  )
  const handle = useRef<PageEditorHandle | null>(null)

  // Falling back is not a failure the author should have to recover from — the
  // markdown editor edits the same bytes.
  const [editorBroken, setEditorBroken] = useState(false)
  useEffect(() => {
    if (editorBroken) setMode("markdown")
  }, [editorBroken])

  const write = async (content: string) => {
    setBusy(true)
    try {
      await api.patch(`/context-hub/pages/${page.id}`, { content })
      toast("Page saved")
      onDone()
    } catch (error) {
      toast(error instanceof Error ? error.message : "Could not save the page", "error")
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    // Read the editor directly rather than the debounced onChange, so a save
    // straight after a keystroke is not a quarter-second stale.
    const next = mode === "blocks" ? (handle.current?.getMarkdown() ?? draft) : draft
    const original = page.content ?? ""

    if (mode === "blocks" && next !== original) {
      const diff = differences(original, next)
      // Only ask when the author changed nothing themselves — otherwise every
      // real edit would raise the dialog.
      if (draft === original && diff.length > 0) {
        setLossy({ next, diff })
        return
      }
    }

    await write(next)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <SegmentedControl
          ariaLabel="Editor"
          value={mode}
          onChange={(next) => {
            // Carry the current text across, so switching does not lose work.
            if (mode === "blocks") setDraft(handle.current?.getMarkdown() ?? draft)
            setMode(next)
          }}
          options={[
            { value: "blocks", label: "Blocks" },
            { value: "markdown", label: "Markdown" },
          ]}
        />
        <span className="text-xs text-faint">
          {mode === "blocks" ? "Press / for headings, tables and code" : "Raw markdown"}
        </span>
      </div>

      <Card className="p-6">
        {mode === "blocks" ? (
          <Suspense fallback={<Skeleton className="h-64" />}>
            <PageEditor
              initialValue={draft}
              handle={handle}
              onChange={setDraft}
              onError={() => setEditorBroken(true)}
              onUpload={uploadImage}
              proxyDomURL={proxyDomURL}
            />
          </Suspense>
        ) : (
          <textarea
            value={draft}
            aria-label="Page content"
            onChange={(event) => setDraft(event.target.value)}
            className="min-h-96 w-full resize-y bg-transparent font-mono text-xs text-foreground outline-none"
          />
        )}
      </Card>

      <div className="flex items-center gap-2">
        <Button disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>

      {lossy && (
        <Modal title="Saving would reformat this page" onClose={() => setLossy(null)}>
          <p className="text-sm text-muted">
            The block editor cannot represent part of this page exactly, so saving would rewrite
            these lines. Nothing has been written yet.
          </p>
          <ul className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-md border border-border p-3">
            {lossy.diff.map((d) => (
              <li key={d.line} className="flex flex-col gap-0.5 font-mono text-[11px]">
                <span className="text-faint">line {d.line}</span>
                <span className="text-danger">− {d.before || "(empty)"}</span>
                <span className="text-success">+ {d.after || "(empty)"}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setLossy(null)
                setMode("markdown")
              }}
            >
              Edit as markdown
            </Button>
            <Button variant="ghost" onClick={() => setLossy(null)}>
              Keep the original
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                const next = lossy.next
                setLossy(null)
                await write(next)
              }}
            >
              Save anyway
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ---------- properties ----------

function Properties({
  page,
  onSave,
}: {
  page: Page
  onSave: (body: Record<string, unknown>, message?: string) => Promise<void>
}) {
  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <h2 className="text-sm font-semibold text-foreground">Properties</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Path">
          <span className="font-mono text-xs text-muted">{page.path}</span>
        </Row>
        <Row label="Type">
          <Dropdown
            items={["doc", "index", "log"].map((type) => ({
              label: type,
              selected: type === page.type,
              onSelect: () => onSave({ type }),
            }))}
            trigger={({ toggle }) => (
              <button
                type="button"
                onClick={toggle}
                className="flex items-center gap-1 rounded px-1 text-sm text-foreground hover:bg-surface-muted"
              >
                {page.type} <ChevronDown className="size-3 text-faint" />
              </button>
            )}
          />
        </Row>
        <Row label="Tags">
          <TagEditor tags={page.tags} onSave={(tags) => onSave({ tags })} />
        </Row>
        <Row label="Updated">
          <span className="text-sm text-muted">{formatDate(page.updatedAt)}</span>
        </Row>
      </div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-16 shrink-0 text-xs text-faint">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function TagEditor({ tags, onSave }: { tags: string[]; onSave: (next: string[]) => void }) {
  const [adding, setAdding] = useState(false)

  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <Badge key={tag}>
          {tag}
          <button
            type="button"
            aria-label={`Remove tag ${tag}`}
            onClick={() => onSave(tags.filter((t) => t !== tag))}
            className="text-faint hover:text-danger"
          >
            ×
          </button>
        </Badge>
      ))}
      {adding ? (
        <input
          autoFocus
          aria-label="New tag"
          placeholder="tag"
          onBlur={() => setAdding(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setAdding(false)
            if (event.key === "Enter") {
              const value = event.currentTarget.value.trim()
              if (value && !tags.includes(value)) onSave([...tags, value])
              setAdding(false)
            }
          }}
          className="w-24 rounded border border-interactive bg-surface px-1.5 py-0.5 text-xs outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rounded px-1.5 py-0.5 text-xs text-faint hover:bg-surface-muted hover:text-foreground"
        >
          + Add
        </button>
      )}
    </div>
  )
}

// ---------- moving ----------

type MoveOutcome = {
  renames: [string, string][]
  rewritten: { path: string; count: number }[]
  links: number
}

/** Moving is not cheap to get wrong: every internal link in the wiki is
 *  relative, so a move rewrites hrefs across other pages. The summary comes
 *  from the server's own dry run, which does the work and rolls back — a count
 *  computed here would be a second implementation that could disagree. */
function MoveDialog({
  page,
  onClose,
  onMoved,
}: {
  page: Page
  onClose: () => void
  onMoved: () => void
}) {
  const toast = useToast()
  const [path, setPath] = useState(page.path)
  const [preview, setPreview] = useState<MoveOutcome | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const call = async (dryRun: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ outcome: MoveOutcome }>(
        `/context-hub/pages/${page.id}/move`,
        { path, dryRun },
      )
      if (dryRun) setPreview(result.outcome)
      else {
        toast(
          result.outcome.links > 0
            ? `Moved, and fixed ${result.outcome.links} link${result.outcome.links === 1 ? "" : "s"}`
            : "Moved",
        )
        onMoved()
      }
    } catch (err) {
      setError(err)
      setPreview(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Move or rename" onClose={onClose}>
      <Field
        label="Path"
        name="path"
        value={path}
        autoFocus
        onChange={(event) => {
          setPath(event.target.value)
          setPreview(null)
        }}
        hint="Where it sits in the tree. Pages sharing a prefix form a folder."
      />

      <ErrorNote error={error} />

      {preview && (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-muted p-3 text-xs">
          <p className="text-foreground">
            {preview.renames.length} page{preview.renames.length === 1 ? "" : "s"} move
            {preview.links > 0
              ? `, and ${preview.links} link${preview.links === 1 ? "" : "s"} in ${preview.rewritten.length} page${preview.rewritten.length === 1 ? "" : "s"} will be rewritten to follow`
              : ", and no links need changing"}
            .
          </p>
          {preview.rewritten.slice(0, 5).map((r) => (
            <p key={r.path} className="font-mono text-faint">
              {r.path} ({r.count})
            </p>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {preview ? (
          <Button disabled={busy} onClick={() => call(false)}>
            {busy ? "Moving…" : "Move"}
          </Button>
        ) : (
          <Button disabled={busy || path === page.path} onClick={() => call(true)}>
            {busy ? "Checking…" : "Preview"}
          </Button>
        )}
      </div>
    </Modal>
  )
}
